import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnDef } from '@tanstack/react-table';

import { dataTableToExcelWorkbook, downloadExcel } from 'services/excel';
import { logError } from 'services/logging/logging';
import { WebEDIRowData } from 'support/types/webediTable';

export type TemplateColumn = ColumnDef<WebEDIRowData, unknown> & { id: string };

export const useDownloadTemplate = () => {
  const { t } = useTranslation();
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const downloadTemplate = (
    columns: Array<TemplateColumn>,
    partnerName: string,
    updatedAt: string | null | undefined,
  ) => {
    setDownloadingTemplate(true);

    const workbook = dataTableToExcelWorkbook(columns, {
      sheetName: t('priceCatalogs:template.sheetName'),
      instructionText: t('priceCatalogs:template.instructions'),
    });

    downloadExcel(
      workbook,
      t('priceCatalogs:template.filename', {
        partnerName,
        date: formatDateToTemplateName(updatedAt),
      }),
    )
      .catch(logError)
      .finally(() => {
        setDownloadingTemplate(false);
      });
  };

  return {
    downloadTemplate,
    downloadingTemplate,
  };
};

/**
 * Formats a date string to a specific template name format.
 * The format is YYYY_MM_DD_HH_MM.
 * @param date - The date string to format.
 * @returns The formatted date string.
 */
const formatDateToTemplateName = (date: string | null | undefined) => {
  if (!date) {
    return '';
  }
  const utcDate = new Date(date);
  const day = String(utcDate.getUTCDate()).padStart(2, '0');
  const month = String(utcDate.getUTCMonth() + 1).padStart(2, '0'); // Months are zero-based
  const year = utcDate.getUTCFullYear();
  const hours = String(utcDate.getUTCHours()).padStart(2, '0');
  const minutes = String(utcDate.getUTCMinutes()).padStart(2, '0');
  return `${year}_${month}_${day}_${hours}_${minutes}`;
};
